/**
 * La file « Suites à traiter » (lot I2.1, ADR-0014) : libellés, filtres de la liste, raison obligatoire, phrases des
 * gestes « écarter » et « clore ».
 *
 * Aucune règle métier ici : le serveur est l'autorité (il refuse une raison trop courte, une suite déjà réglée, une
 * révision périmée). L'écran n'en reflète que ce qu'il faut pour ne pas proposer un geste voué au refus.
 * Écarter ou clore est DÉFINITIF : le serveur interdit toute réouverture, et l'écran le dit avant le geste.
 *
 * Fonctions pures, sans dépendance au navigateur.
 */
import type { EtatSuiteBon, FiltreSuitesATraiter, GenreSuiteBon, SuiteATraiter } from '../api/client';
import type { DroitsBons } from './droits';
import { GENRES_SUITE, LIBELLE_ETAT_SUITE, LIBELLE_SUITE, jourFr } from './interventions';

/** La raison d'un geste : trois caractères au moins, cinq cents au plus (le serveur applique les mêmes bornes). */
export const RAISON_MIN = 3;
export const RAISON_MAX = 500;

/** Combien de suites on charge d'un coup ; « Afficher plus » en demande autant. */
export const TAILLE_PAGE_SUITES = 100;

export interface ChipEtatSuite {
  readonly id: string;
  readonly libelle: string;
  /** États demandés au serveur. */
  readonly statuts: readonly EtatSuiteBon[];
}

/** Les filtres d'état : « À traiter » d'abord, c'est ce que l'on cherche le plus ; « Toutes » les compte toutes. */
export const CHIPS_ETAT_SUITE: readonly ChipEtatSuite[] = [
  { id: 'a-traiter', libelle: 'À traiter', statuts: ['OPEN'] },
  { id: 'ecartees', libelle: 'Écartées', statuts: ['DISMISSED'] },
  { id: 'soldees', libelle: 'Soldées', statuts: ['CLOSED'] },
  { id: 'toutes', libelle: 'Toutes', statuts: ['OPEN', 'DISMISSED', 'CLOSED', 'QUALIFIED', 'CONVERTED'] },
];

/** Le filtre d'état choisi dans l'adresse ; un identifiant inconnu rend « À traiter ». */
export function chipEtatParId(id: string | null): ChipEtatSuite {
  return CHIPS_ETAT_SUITE.find((c) => c.id === id) ?? CHIPS_ETAT_SUITE[0]!;
}

/** Les types proposés en filtre : tous, ou l'un des six. */
export const CHIPS_TYPE_SUITE: readonly { readonly id: GenreSuiteBon | 'tous'; readonly libelle: string }[] = [
  { id: 'tous', libelle: 'Tous les types' },
  ...GENRES_SUITE.map((id) => ({ id, libelle: LIBELLE_SUITE[id] })),
];

/** Le type choisi dans l'adresse ; un identifiant inconnu ou absent = tous les types (`null`). */
export function typeParId(id: string | null): GenreSuiteBon | null {
  return GENRES_SUITE.find((g) => g === id) ?? null;
}

export interface ParametresFile {
  readonly q: string;
  readonly etat: ChipEtatSuite;
  readonly type: GenreSuiteBon | null;
  readonly take: number;
  readonly skip?: number;
}

/** Ce que l'écran demande au serveur : les états toujours dits (jamais un défaut implicite), le reste seulement s'il est choisi. */
export function filtreDeLaFile(p: ParametresFile): FiltreSuitesATraiter {
  const q = p.q.trim();
  return {
    ...(q !== '' ? { q } : {}),
    statuses: p.etat.statuts.join(','),
    ...(p.type ? { kinds: p.type } : {}),
    take: p.take,
    ...(p.skip !== undefined && p.skip > 0 ? { skip: p.skip } : {}),
  };
}

/** Le début d'un commentaire pour la liste : une seule ligne, tronquée avec des points de suspension. */
export function debutCommentaire(commentaire: string, max = 110): string {
  const ligne = commentaire.replace(/\s+/g, ' ').trim();
  return ligne.length <= max ? ligne : `${ligne.slice(0, max - 1).trimEnd()}…`;
}

export type GesteSuite = 'ecarter' | 'clore';

interface TextesGeste {
  readonly bouton: string;
  readonly titre: string;
  readonly sousTitre: string;
  readonly question: string;
  readonly placeholder: string;
  readonly confirmer: string;
  readonly verbeAuParticipe: string;
}

/** Les mots de chaque geste. Les deux sont définitifs : le sous-titre le dit avant que l'on clique. */
export const TEXTES_GESTE: Readonly<Record<GesteSuite, TextesGeste>> = {
  ecarter: {
    bouton: 'Écarter',
    titre: 'Écarter cette suite',
    sousTitre:
      '« Elle ne sert plus à rien. » C’est DÉFINITIF : une suite écartée ne se rouvre pas, et rien n’est créé à sa place.',
    question: 'Pourquoi l’écartez-vous ?',
    placeholder: 'Le client a refusé le devis, déjà traité par ailleurs, machine vendue…',
    confirmer: 'Écarter la suite',
    verbeAuParticipe: 'écartée',
  },
  clore: {
    bouton: 'Clore',
    titre: 'Clore cette suite',
    sousTitre:
      '« C’est fait. » C’est DÉFINITIF : une suite soldée ne se rouvre pas. Aucun devis, aucune commande ni aucune intervention n’est créé : c’est à vous de les faire.',
    question: 'Qu’est-ce qui a été fait ?',
    placeholder: 'Devis envoyé le 16/10, pièce commandée, intervention prévue…',
    confirmer: 'Clore la suite',
    verbeAuParticipe: 'soldée',
  },
};

export type RaisonLue = { readonly ok: true; readonly raison: string } | { readonly ok: false; readonly message: string };

/** La raison saisie : rognée, trois caractères au moins, cinq cents au plus. */
export function validerRaison(brut: string): RaisonLue {
  const raison = brut.trim();
  if (raison.length < RAISON_MIN) {
    return { ok: false, message: `La raison est obligatoire (${RAISON_MIN} caractères au moins).` };
  }
  if (raison.length > RAISON_MAX) {
    return { ok: false, message: `La raison est trop longue : ${RAISON_MAX} caractères au plus (${raison.length} saisis).` };
  }
  return { ok: true, raison };
}

/** On peut écarter ou clore une suite OUVERTE, si l'on a le droit de saisir les bons (c'est le droit du serveur). */
export function peutTrierUneSuite(suite: Pick<SuiteATraiter, 'status'>, droits: Pick<DroitsBons, 'peutSaisirBons'>): boolean {
  return suite.status === 'OPEN' && droits.peutSaisirBons;
}

/** Ce que dit le détail d'une suite sur son état : pourquoi on ne peut plus la traiter, ou comment la traiter. */
export function phraseEtatSuite(suite: Pick<SuiteATraiter, 'status' | 'resolutionNote' | 'resolvedAt'>): string {
  switch (suite.status) {
    case 'OPEN':
      return 'À traiter. Aucun devis ni aucune commande ne part tout seul : ces modules n’existent pas encore. Une intervention se crée par « Convertir en intervention » ; sinon écartez la suite si elle ne sert plus à rien, clôturez-la quand c’est fait.';
    case 'DISMISSED':
    case 'CLOSED': {
      const verbe = suite.status === 'DISMISSED' ? 'Écartée' : 'Soldée';
      const date = suite.resolvedAt ? ` le ${jourFr(suite.resolvedAt)}` : '';
      return `${verbe}${date}${suite.resolutionNote ? ` : ${suite.resolutionNote}` : ''}`;
    }
    default:
      return LIBELLE_ETAT_SUITE[suite.status];
  }
}

/** Le message de succès d'un geste. */
export function phraseSuccesGeste(geste: GesteSuite, suite: Pick<SuiteATraiter, 'kind' | 'intervention'>): string {
  return `Suite « ${LIBELLE_SUITE[suite.kind]} » du bon ${suite.intervention.number} ${TEXTES_GESTE[geste].verbeAuParticipe}.`;
}

/** Ce que l'écran dit à qui ne peut que lire. */
export const PHRASE_LECTURE_SEULE =
  'Vous consultez les suites en lecture seule : les écarter ou les clore demande le droit de saisir les interventions.';

/** Le résumé du filtre, pour le compteur de la liste : « 3 suites à traiter », « 1 suite écartée sur 4 »… */
export function phraseCompte(affichees: number, total: number, etat: ChipEtatSuite): string {
  const pluriel = total > 1;
  const nom = `suite${pluriel ? 's' : ''}`;
  const qualificatif =
    etat.id === 'a-traiter' ? 'à traiter' : etat.id === 'ecartees' ? `écartée${pluriel ? 's' : ''}` : etat.id === 'soldees' ? `soldée${pluriel ? 's' : ''}` : '';
  const base = `${affichees}${total > affichees ? ` sur ${total}` : ''} ${nom}${qualificatif ? ` ${qualificatif}` : ''}`;
  return base;
}
